/**
 * M15: Campaign list + inline metadata editor inside ContentSwitcherPanel.
 * Clicking a campaign row opens CampaignEditor (like BlogsTab/BlogPostEditor).
 * Visual rendering happens in CampaignCanvas.
 */
import { useEffect, useMemo, useState } from "react"
import { ArrowLeft, Plus, Search, X } from "lucide-react"
import { Switch } from "@/components/ui/switch"
import { toast } from "sonner"
import debounce from "lodash.debounce"
import {
  useCampaigns,
  useCampaign,
  useCreateCampaign,
  useUpdateCampaign,
  isCampaignSlugTaken,
} from "@/hooks/useCampaigns"
import { blocksToContentSections } from "@/components/website-builder/BlogsTab"
import { getDefaultBlockContent } from "@/components/website-builder/blocks/registry"
import type { PageSection } from "@/templates/types"
import { generateSlug } from "@/lib/slugUtils"
import type { Json } from "@/types/database"
import type { CampaignLiveData } from "./CampaignCanvas"
import type { BlockType } from "./blocks/types"
import { BlockPicker } from "./BlockPicker"

const LABEL: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  textTransform: "uppercase" as const,
  color: "rgba(255,255,255,0.35)",
  marginBottom: 5,
  display: "block",
}

const INPUT: React.CSSProperties = {
  width: "100%",
  background: "rgba(255,255,255,0.05)",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: 8,
  padding: "7px 10px",
  color: "#fff",
  fontSize: 12,
  outline: "none",
  boxSizing: "border-box" as const,
}

function normalizeSlug(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
}

function publishedBadge(isPublished: boolean) {
  return (
    <span
      style={{
        background: isPublished ? "rgba(34,197,94,0.15)" : "rgba(255,255,255,0.1)",
        color: isPublished ? "#22c55e" : "rgba(255,255,255,0.5)",
        fontSize: 9,
        fontWeight: 600,
        padding: "2px 7px",
        borderRadius: 20,
        textTransform: "uppercase" as const,
        letterSpacing: "0.06em",
        flexShrink: 0,
      }}
    >
      {isPublished ? "published" : "draft"}
    </span>
  )
}

function contentSectionsToBlocks(contentSections: Json, orgId: string): PageSection[] {
  const arr = Array.isArray(contentSections) ? contentSections : []
  return (arr as Record<string, unknown>[]).map((item, i) => ({
    id: String(item.id ?? crypto.randomUUID()),
    section_type: String(item.section_type ?? "paragraph"),
    order_index: typeof item.order_index === "number" ? item.order_index : i,
    content: (item.content as Record<string, unknown>) ?? null,
    page_id: "",
    org_id: orgId,
    is_visible: true,
  }))
}

function centsToInput(cents: number): string {
  return (cents / 100).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })
}

function parseGoalToCents(text: string): number {
  const n = Number.parseFloat(text.replace(/[$,\s]/g, ""))
  if (Number.isNaN(n) || n < 0) return 0
  return Math.round(n * 100)
}

// ─── CampaignEditor ──────────────────────────────────────────────────────────

interface CampaignEditorProps {
  orgId: string
  campaignId: string
  onBack: () => void
  onCampaignLiveUpdate: (data: CampaignLiveData) => void
  onAddBlock?: (type: BlockType) => void
}

function CampaignEditor({
  orgId,
  campaignId,
  onBack,
  onCampaignLiveUpdate,
  onAddBlock,
}: CampaignEditorProps) {
  const { campaign, loading } = useCampaign(orgId, campaignId)
  const { updateCampaign } = useUpdateCampaign(orgId)

  const [title, setTitle] = useState("")
  const [slug, setSlug] = useState("")
  const [slugTouched, setSlugTouched] = useState(false)
  const [slugError, setSlugError] = useState<string | null>(null)
  const [goalInput, setGoalInput] = useState("0")
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")
  const [isPublished, setIsPublished] = useState(false)
  const [blocks, setBlocks] = useState<PageSection[]>([])
  const [initialized, setInitialized] = useState(false)
  const [blockPickerOpen, setBlockPickerOpen] = useState(false)

  useEffect(() => {
    setInitialized(false)
  }, [campaignId])

  useEffect(() => {
    if (!campaign || initialized) return
    setTitle(campaign.title)
    setSlug(campaign.slug)
    setSlugTouched(false)
    setSlugError(null)
    setGoalInput(centsToInput(campaign.goal_cents))
    setStartDate(campaign.start_date ?? "")
    setEndDate(campaign.end_date ?? "")
    setIsPublished(campaign.is_published)
    const loadedBlocks = contentSectionsToBlocks(campaign.content_sections ?? [], orgId)
    setBlocks(loadedBlocks)
    setInitialized(true)
    onCampaignLiveUpdate({
      id: campaign.id,
      title: campaign.title,
      slug: campaign.slug,
      goal_cents: campaign.goal_cents,
      start_date: campaign.start_date ?? null,
      end_date: campaign.end_date ?? null,
      is_published: campaign.is_published,
      blocks: loadedBlocks,
    })
  }, [campaign, orgId, initialized]) // eslint-disable-line react-hooks/exhaustive-deps

  const debouncedSave = useMemo(
    () =>
      debounce(async (updates: Record<string, unknown>) => {
        await updateCampaign(campaignId, updates)
      }, 600),
    [campaignId, updateCampaign]
  )

  function emitLive(overrides: Partial<{
    title: string; slug: string; goal_cents: number
    start_date: string | null; end_date: string | null
    is_published: boolean; blocks: PageSection[]
  }> = {}) {
    onCampaignLiveUpdate({
      id: campaignId,
      title: overrides.title ?? title,
      slug: overrides.slug ?? slug,
      goal_cents: overrides.goal_cents ?? parseGoalToCents(goalInput),
      start_date: overrides.start_date !== undefined ? overrides.start_date : (startDate || null),
      end_date: overrides.end_date !== undefined ? overrides.end_date : (endDate || null),
      is_published: overrides.is_published ?? isPublished,
      blocks: overrides.blocks ?? blocks,
    })
  }

  function handleTitleChange(val: string) {
    setTitle(val)
    const nextSlug = slugTouched ? slug : normalizeSlug(generateSlug(val))
    if (!slugTouched) setSlug(nextSlug)
    emitLive({ title: val, slug: nextSlug })
    debouncedSave({ title: val.trim(), ...(!slugTouched ? { slug: nextSlug } : {}) })
  }

  function handleSlugChange(val: string) {
    setSlugTouched(true)
    const n = normalizeSlug(val)
    setSlug(n)
    setSlugError(null)
    emitLive({ slug: n })
    debouncedSave({ slug: n })
  }

  async function handleSlugBlur() {
    const n = normalizeSlug(slug)
    if (!n) { setSlugError("Slug is required"); return }
    const taken = await isCampaignSlugTaken(orgId, n, campaignId)
    if (taken) setSlugError("This URL is already used by another campaign.")
    else setSlugError(null)
  }

  function handleGoalBlur() {
    const cents = parseGoalToCents(goalInput)
    setGoalInput(centsToInput(cents))
    emitLive({ goal_cents: cents })
    void updateCampaign(campaignId, { goal_cents: cents })
  }

  function handleStartDate(val: string) {
    setStartDate(val)
    emitLive({ start_date: val || null })
    debouncedSave({ start_date: val || null })
  }

  function handleEndDate(val: string) {
    setEndDate(val)
    emitLive({ end_date: val || null })
    debouncedSave({ end_date: val || null })
  }

  async function handlePublishToggle(checked: boolean) {
    setIsPublished(checked)
    emitLive({ is_published: checked })
    await updateCampaign(campaignId, { is_published: checked })
  }

  function handleBlockPickerSelect(type: BlockType) {
    setBlockPickerOpen(false)
    const newBlock: PageSection = {
      id: crypto.randomUUID(),
      section_type: type,
      order_index: blocks.length,
      content: getDefaultBlockContent(type) as Record<string, unknown>,
      page_id: "",
      org_id: orgId,
      is_visible: true,
    }
    const newBlocks = [...blocks, newBlock].map((b, i) => ({ ...b, order_index: i }))
    setBlocks(newBlocks)
    emitLive({ blocks: newBlocks })
    if (onAddBlock) onAddBlock(type)
  }

  if (loading && !initialized) {
    return (
      <div
        style={{
          padding: 24,
          color: "rgba(255,255,255,0.3)",
          fontSize: 12,
          textAlign: "center",
        }}
      >
        Loading…
      </div>
    )
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
      {/* Back header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "10px 12px",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        <button
          type="button"
          onClick={onBack}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "rgba(255,255,255,0.4)",
            padding: 4,
            display: "flex",
            alignItems: "center",
          }}
          aria-label="Back to campaigns"
        >
          <ArrowLeft size={14} />
        </button>
        <span
          style={{
            flex: 1,
            fontSize: 12,
            fontWeight: 600,
            color: "#fff",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {title || "Untitled campaign"}
        </span>
        {publishedBadge(isPublished)}
      </div>

      {/* Metadata form */}
      <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
        <div>
          <label style={LABEL}>Title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => handleTitleChange(e.target.value)}
            style={INPUT}
            placeholder="Spring fundraiser"
          />
        </div>
        <div>
          <label style={LABEL}>URL slug</label>
          <input
            type="text"
            value={slug}
            onChange={(e) => handleSlugChange(e.target.value)}
            onBlur={() => void handleSlugBlur()}
            style={{ ...INPUT, fontFamily: "monospace" }}
            placeholder="spring-fundraiser"
          />
          {slugError && (
            <span style={{ display: "block", marginTop: 4, fontSize: 11, color: "#f87171" }}>
              {slugError}
            </span>
          )}
        </div>
        <div>
          <label style={LABEL}>Goal amount (USD)</label>
          <input
            type="text"
            inputMode="decimal"
            value={goalInput}
            onChange={(e) => setGoalInput(e.target.value)}
            onBlur={handleGoalBlur}
            style={INPUT}
          />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div>
            <label style={LABEL}>Start date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => handleStartDate(e.target.value)}
              style={INPUT}
            />
          </div>
          <div>
            <label style={LABEL}>End date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => handleEndDate(e.target.value)}
              style={INPUT}
            />
          </div>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 8,
            padding: "8px 12px",
          }}
        >
          <span style={{ fontSize: 12, color: "rgba(255,255,255,0.7)" }}>
            Published
          </span>
          <Switch checked={isPublished} onCheckedChange={(v) => void handlePublishToggle(v)} />
        </div>
      </div>

      <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", margin: "0 16px" }} />

      {/* Add block */}
      <div style={{ padding: "12px 16px" }}>
        <button
          type="button"
          onClick={() => setBlockPickerOpen(true)}
          style={{
            width: "100%",
            padding: "7px 12px",
            background: "rgba(255,255,255,0.05)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 8,
            color: "rgba(255,255,255,0.6)",
            fontSize: 12,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
          }}
        >
          <Plus size={12} />
          Add block
        </button>
      </div>

      <BlockPicker
        open={blockPickerOpen}
        onClose={() => setBlockPickerOpen(false)}
        onSelect={handleBlockPickerSelect}
      />
    </div>
  )
}

// ─── CampaignsTab ────────────────────────────────────────────────────────────

export interface CampaignsTabProps {
  orgId: string
  activeCampaignId: string | null
  onSelectCampaign: (campaignId: string | null) => void
  onCampaignLiveUpdate: (data: CampaignLiveData) => void
  onAddBlock?: (type: BlockType) => void
}

export function CampaignsTab({
  orgId,
  activeCampaignId,
  onSelectCampaign,
  onCampaignLiveUpdate,
  onAddBlock,
}: CampaignsTabProps) {
  const { campaigns, loading, refresh } = useCampaigns(orgId)
  const { createCampaign, loading: creating } = useCreateCampaign(orgId)
  const [search, setSearch] = useState("")
  const [showCreate, setShowCreate] = useState(false)
  const [showEditor, setShowEditor] = useState(false)
  const [newTitle, setNewTitle] = useState("")
  const [newSlug, setNewSlug] = useState("")
  const [slugTouched, setSlugTouched] = useState(false)
  const [slugError, setSlugError] = useState<string | null>(null)
  const [goalDollars, setGoalDollars] = useState("1,000")
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")

  // When activeCampaignId changes externally (e.g. new campaign created), show editor
  useEffect(() => {
    if (activeCampaignId) setShowEditor(true)
    else setShowEditor(false)
  }, [activeCampaignId])

  const filtered = search
    ? campaigns.filter((c) => c.title.toLowerCase().includes(search.toLowerCase()))
    : campaigns

  function handleNewTitle(v: string) {
    setNewTitle(v)
    if (!slugTouched) setNewSlug(normalizeSlug(generateSlug(v)))
  }

  async function handleCreateSubmit(e: React.FormEvent) {
    e.preventDefault()
    const title = newTitle.trim()
    if (!title) { toast.error("Title is required"); return }
    const sl = normalizeSlug(newSlug || generateSlug(title))
    if (!sl) { setSlugError("Enter a valid URL slug."); return }
    const taken = await isCampaignSlugTaken(orgId, sl, null)
    if (taken) { setSlugError("This URL is already used by another campaign."); return }
    setSlugError(null)
    const cents = Math.round(Number.parseFloat(goalDollars.replace(/[$,\s]/g, "")) * 100)
    if (!Number.isFinite(cents) || cents <= 0) { toast.error("Enter a valid goal amount"); return }

    const heroBlock: PageSection = {
      id: crypto.randomUUID(),
      section_type: "hero",
      order_index: 0,
      content: getDefaultBlockContent("hero") as Record<string, unknown>,
      page_id: "",
      org_id: orgId,
      is_visible: true,
    }
    const headingBlock: PageSection = {
      id: crypto.randomUUID(),
      section_type: "heading",
      order_index: 1,
      content: getDefaultBlockContent("heading") as Record<string, unknown>,
      page_id: "",
      org_id: orgId,
      is_visible: true,
    }

    const row = await createCampaign({
      title,
      slug: sl,
      goal_cents: cents,
      start_date: startDate || null,
      end_date: endDate || null,
      content_sections: blocksToContentSections([heroBlock, headingBlock]) as Json,
    })

    if (!row) { toast.error("Could not create campaign"); return }
    toast.success("Campaign created")
    setShowCreate(false)
    setNewTitle("")
    setNewSlug("")
    setSlugTouched(false)
    setGoalDollars("1,000")
    setStartDate("")
    setEndDate("")
    await refresh()
    onSelectCampaign(row.id)
  }

  // Show inline editor when a campaign is active
  if (showEditor && activeCampaignId) {
    return (
      <CampaignEditor
        orgId={orgId}
        campaignId={activeCampaignId}
        onBack={() => setShowEditor(false)}
        onCampaignLiveUpdate={onCampaignLiveUpdate}
        onAddBlock={onAddBlock}
      />
    )
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
      {/* Search */}
      <div style={{ padding: "12px 16px" }}>
        <div style={{ position: "relative" }}>
          <Search
            size={11}
            color="rgba(255,255,255,0.3)"
            style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)" }}
          />
          <input
            type="text"
            placeholder="Search campaigns…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ ...INPUT, paddingLeft: 26 }}
            data-testid="campaigns-search"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              style={{
                position: "absolute",
                right: 6,
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                padding: 2,
                cursor: "pointer",
                color: "rgba(255,255,255,0.3)",
                display: "flex",
              }}
            >
              <X size={11} />
            </button>
          )}
        </div>
      </div>

      <div style={{ height: 1, background: "rgba(255,255,255,0.06)", margin: "0 0 4px" }} />

      <div style={{ padding: "2px 0" }}>
        {loading ? (
          <div
            style={{
              padding: "20px 16px",
              color: "rgba(255,255,255,0.3)",
              fontSize: 12,
              textAlign: "center",
            }}
          >
            Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div
            style={{
              padding: "20px 16px",
              color: "rgba(255,255,255,0.3)",
              fontSize: 12,
              textAlign: "center",
            }}
          >
            {search ? "No campaigns match" : "No campaigns yet"}
          </div>
        ) : (
          filtered.map((c) => {
            const active = c.id === activeCampaignId
            return (
              <button
                key={c.id}
                type="button"
                data-testid={`campaign-row-${c.slug}`}
                onClick={() => {
                  onSelectCampaign(c.id)
                  setShowEditor(true)
                }}
                className="campaign-row"
                style={{
                  width: "100%",
                  padding: "8px 16px",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  background: active ? "rgba(255,255,255,0.06)" : "transparent",
                  border: "none",
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <span style={{ fontSize: 14, flex: "none" }}>🎯</span>
                <span
                  style={{
                    flex: 1,
                    fontSize: 12,
                    color: active ? "#fff" : "rgba(255,255,255,0.75)",
                    fontWeight: active ? 500 : 400,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap" as const,
                    minWidth: 0,
                  }}
                >
                  {c.title}
                </span>
                {publishedBadge(c.is_published)}
              </button>
            )
          })
        )}
      </div>

      {showCreate ? (
        <form
          onSubmit={(e) => void handleCreateSubmit(e)}
          style={{
            borderTop: "1px solid rgba(255,255,255,0.07)",
            padding: 16,
            background: "rgba(255,255,255,0.02)",
          }}
          data-testid="campaign-create-form"
        >
          <div style={{ fontSize: 12, fontWeight: 600, color: "#fff", marginBottom: 12 }}>New campaign</div>
          <div style={{ marginBottom: 10 }}>
            <label style={LABEL}>Title</label>
            <input
              required
              value={newTitle}
              onChange={(e) => handleNewTitle(e.target.value)}
              style={INPUT}
              placeholder="Spring fundraiser"
            />
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={LABEL}>URL slug</label>
            <input
              value={newSlug}
              onChange={(e) => {
                setSlugTouched(true)
                setNewSlug(normalizeSlug(e.target.value))
                setSlugError(null)
              }}
              style={{ ...INPUT, fontFamily: "monospace" }}
              placeholder="spring-fundraiser"
            />
            {slugError && (
              <span style={{ display: "block", marginTop: 6, fontSize: 11, color: "#f87171" }}>
                {slugError}
              </span>
            )}
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={LABEL}>Goal amount (USD)</label>
            <input
              value={goalDollars}
              onChange={(e) => setGoalDollars(e.target.value)}
              style={INPUT}
              inputMode="decimal"
            />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
            <div>
              <label style={LABEL}>Start (optional)</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={INPUT}
              />
            </div>
            <div>
              <label style={LABEL}>End (optional)</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={INPUT}
              />
            </div>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button
              type="button"
              onClick={() => { setShowCreate(false); setSlugError(null) }}
              style={{
                flex: 1,
                padding: "6px 10px",
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 7,
                color: "rgba(255,255,255,0.5)",
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={creating}
              style={{
                flex: 1,
                padding: "6px 10px",
                background: creating ? "rgba(255,255,255,0.08)" : "rgba(59,130,246,0.9)",
                border: "none",
                borderRadius: 7,
                color: "#fff",
                fontSize: 12,
                fontWeight: 600,
                cursor: creating ? "not-allowed" : "pointer",
              }}
            >
              {creating ? "Creating…" : "Create"}
            </button>
          </div>
        </form>
      ) : (
        <>
          <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", margin: "4px 0 0" }} />
          <button
            type="button"
            data-testid="campaigns-new-button"
            onClick={() => setShowCreate(true)}
            style={{
              padding: "8px 16px",
              fontSize: 12,
              color: "rgba(255,255,255,0.35)",
              background: "transparent",
              border: "none",
              width: "100%",
              textAlign: "left",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span>＋</span>
            <span>New campaign</span>
          </button>
        </>
      )}

      <style>{`
        .campaign-row:hover { background: rgba(255,255,255,0.04) !important; }
      `}</style>
    </div>
  )
}
